/**
 * Fullscreen helpers.
 *
 * Browsers only grant fullscreen from a user gesture, so `requestFullscreen`
 * must be called synchronously inside a click handler. Re-entering after an
 * involuntary exit therefore cannot be automatic — the UI has to show a button.
 * That is by design here: v1 logs the exit and asks the student to return, it
 * never fights the browser or ends the exam.
 */

export function isFullscreen(): boolean {
  return document.fullscreenElement !== null;
}

export function fullscreenSupported(): boolean {
  return (
    typeof document.documentElement.requestFullscreen === "function" &&
    (document.fullscreenEnabled ?? true)
  );
}

/** Must be called from within a user gesture. Resolves false if denied. */
export async function enterFullscreen(): Promise<boolean> {
  if (isFullscreen()) return true;
  try {
    await document.documentElement.requestFullscreen({ navigationUI: "hide" });
    return true;
  } catch {
    return false;
  }
}

export async function exitFullscreen(): Promise<void> {
  if (!isFullscreen()) return;
  try {
    await document.exitFullscreen();
  } catch {
    /* already out */
  }
}

export function onFullscreenChange(fn: (active: boolean) => void): () => void {
  const handler = () => fn(isFullscreen());
  document.addEventListener("fullscreenchange", handler);
  return () => document.removeEventListener("fullscreenchange", handler);
}
